import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from "react";

import { useKaiAttachments } from "@/hooks/useKaiAttachments";
import { useKaiFileDrop } from "@/hooks/useKaiFileDrop";
import type { AttachmentEntry } from "@/hooks/useKaiAttachments";
import { useMainScrollEdge } from "@/hooks/useMainScrollEdge";
import { useKaiWelcomePrompts } from "@/hooks/useKaiWelcomePrompts";
import { useKaiSlashCommands } from "@/hooks/useKaiSlashCommands";
import type { KaiSlashCommand } from "@/services/kaiApi";
import { useKaiChat } from "./KaiChatContext";
import { KaiComposer } from "./KaiComposer";
import { KaiFileDropOverlay } from "./KaiFileDropOverlay";
import type { KaiEntityTag } from "./kaiEntityTags";
import type { KaiSlashSelection } from "./kaiTypes";
import { KaiWelcome } from "./KaiWelcome";
import {
  KaiFollowUps,
  KaiLiveTurn,
  KaiThreadItems,
} from "./KaiLiveTurn";

function ComposerFooter({
  draft,
  onDraftChange,
  onSend,
  onStop,
  disabled,
  isGenerating,
  awaitingInput,
  entityTags,
  onEntityTagsChange,
  slashCommands,
  onSlashCommandChange,
  attachmentEntries,
  onAddFiles,
  onRemoveAttachment,
  attachmentsBlocking,
  className,
}: {
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: (
    text: string,
    tags: KaiEntityTag[],
    attachmentIds: string[],
    slash: KaiSlashSelection | null,
  ) => void;
  onStop?: () => void;
  disabled?: boolean;
  isGenerating?: boolean;
  awaitingInput?: boolean;
  entityTags: KaiEntityTag[];
  onEntityTagsChange: (tags: KaiEntityTag[]) => void;
  slashCommands: KaiSlashCommand[];
  onSlashCommandChange: (slash: KaiSlashSelection | null) => void;
  attachmentEntries: AttachmentEntry[];
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
  attachmentsBlocking: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <KaiComposer
        value={draft}
        onChange={onDraftChange}
        onSend={onSend}
        onStop={onStop}
        disabled={disabled}
        isGenerating={isGenerating}
        awaitingInput={awaitingInput}
        entityTags={entityTags}
        onEntityTagsChange={onEntityTagsChange}
        slashCommands={slashCommands}
        onSlashCommandChange={onSlashCommandChange}
        attachmentEntries={attachmentEntries}
        onAddFiles={onAddFiles}
        onRemoveAttachment={onRemoveAttachment}
        attachmentsBlocking={attachmentsBlocking}
        placeholder="Ask Kai…  ·  @ to tag  ·  / for commands"
      />
      <p className="mt-2 hidden text-center text-[11px] text-muted-foreground md:block">
        Kai can make mistakes. Verify important details.
      </p>
    </div>
  );
}

function ThreadFadeScroller({
  scrollRef,
  className,
  children,
}: {
  scrollRef: Ref<HTMLDivElement>;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="scroll-fade-frame scroll-fade-frame--surface-mask scroll-fade-frame--kai-thread relative flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} className={className}>
        {children}
      </div>
    </div>
  );
}

/** Main chat thread + composer — input under prompts on welcome, docks to bottom in chat. */
export function KaiChatPanel() {
  const kai = useKaiChat();
  const [draft, setDraft] = useState("");
  const [entityTags, setEntityTags] = useState<KaiEntityTag[]>([]);
  const [, setSlashSelection] = useState<KaiSlashSelection | null>(null);
  const slashCommands = useKaiSlashCommands();

  const isGenerating = kai?.isGenerating ?? false;
  const isWelcome = kai?.isWelcome ?? true;
  const sendMessage = kai?.sendMessage;
  const streamingContent = kai?.streamingContent ?? "";
  const toolCount = kai?.toolActivity.length ?? 0;
  const planTitle = kai?.planProgress?.title;
  const mcpStatus = kai?.mcpStatus ?? "";
  const awaitingInput = kai?.awaitingClarification ?? false;
  const clarificationCount = kai?.pendingClarifications.length ?? 0;
  const activeId = kai?.activeId ?? null;
  const ensureConversation = kai?.ensureConversation;

  const isDocked = !isWelcome;
  const threadRef = useMainScrollEdge<HTMLDivElement>(isDocked);
  const welcomePrompts = useKaiWelcomePrompts(isWelcome);

  const resolveConversationId = useCallback(
    () => (ensureConversation ? ensureConversation() : Promise.resolve(null)),
    [ensureConversation],
  );
  const attachments = useKaiAttachments({ resolveConversationId });
  const { isDragging, dropHandlers } = useKaiFileDrop({
    onFiles: attachments.addFiles,
    disabled: isGenerating,
  });

  // Clear pending files when moving to a different conversation or a new chat.
  // Skip the null→id transition, which happens when an upload creates the
  // conversation for files just added.
  const prevActiveIdRef = useRef<string | null>(activeId);
  const clearAttachments = attachments.clear;
  useEffect(() => {
    const prev = prevActiveIdRef.current;
    if (prev !== activeId && prev !== null) clearAttachments();
    prevActiveIdRef.current = activeId;
  }, [activeId, clearAttachments]);

  const handleSend = useCallback(
    (
      text: string,
      tags: KaiEntityTag[],
      attachmentIds: string[],
      slash: KaiSlashSelection | null,
    ) => {
      if (!sendMessage) return;
      sendMessage(
        text,
        tags,
        attachmentIds,
        attachments.snapshotMetadata(),
        slash,
      );
      attachments.clear();
      setSlashSelection(null);
    },
    [sendMessage, attachments],
  );

  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isDocked) scrollToBottom();
  }, [
    kai?.thread.length,
    isGenerating,
    streamingContent,
    toolCount,
    planTitle,
    mcpStatus,
    clarificationCount,
    isDocked,
    scrollToBottom,
  ]);

  if (!kai || !sendMessage) return null;

  const composerProps = {
    draft,
    onDraftChange: setDraft,
    onSend: handleSend,
    onStop: kai.stopGeneration,
    disabled: isGenerating && !awaitingInput,
    isGenerating,
    awaitingInput,
    entityTags,
    onEntityTagsChange: setEntityTags,
    slashCommands,
    onSlashCommandChange: setSlashSelection,
    attachmentEntries: attachments.entries,
    onAddFiles: attachments.addFiles,
    onRemoveAttachment: attachments.removeAt,
    attachmentsBlocking: attachments.blocking,
  };

  return (
    <div
      {...dropHandlers}
      className={`relative mx-auto flex w-full flex-1 flex-col ${
        isDocked
          ? "h-full min-h-0 max-w-5xl"
          : "h-full min-h-0 max-w-3xl"
      }`}
    >
      {isDragging && <KaiFileDropOverlay />}
      {isDocked ? (
        <>
          <ThreadFadeScroller
            scrollRef={threadRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-1 [overflow-anchor:none]"
          >
            <div className="flex flex-col gap-4 pb-4 pt-2">
              <KaiThreadItems />
              <KaiLiveTurn />
              {kai.error && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive">
                  {kai.error}
                </div>
              )}
              {!isGenerating && (
                <KaiFollowUps
                  suggestions={kai.followUpSuggestions}
                  onSelect={sendMessage}
                />
              )}
            </div>
          </ThreadFadeScroller>

          <ComposerFooter
            {...composerProps}
            className="kai-composer-dock shrink-0 px-4 pb-0 pt-2 md:pb-1 md:pt-3 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-500"
          />
        </>
      ) : (
        <div className="flex h-full min-h-0 flex-col overflow-hidden px-4">
          <ThreadFadeScroller
            scrollRef={threadRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain py-4 md:items-center md:justify-center md:overflow-hidden md:pb-10 md:pt-6 [overflow-anchor:none]"
          >
            <div className="kai-welcome-from-splash mx-auto my-auto w-full max-w-2xl md:text-center">
              <KaiWelcome
                prompts={welcomePrompts.prompts}
                loading={welcomePrompts.loading}
                onSelectPrompt={sendMessage}
              />
              <ComposerFooter
                {...composerProps}
                className="mt-6 hidden md:block"
              />
            </div>
          </ThreadFadeScroller>

          <ComposerFooter
            {...composerProps}
            className="kai-composer-dock shrink-0 pb-0 pt-2 md:hidden"
          />
        </div>
      )}
    </div>
  );
}
